// SymbolEditor.jsx — SIDC attribute editor for a placed military symbol feature.

import React, {useMemo, useState, useEffect, useRef} from 'react';
import {
    Box, Stack, Typography, Select, MenuItem,
    Divider, IconButton, Tooltip, TextField,
    Accordion, AccordionSummary, AccordionDetails,
    CircularProgress,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import * as mgrsLib from 'mgrs';
import {MSLookup, Modifiers} from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import {useRendererSettings} from '../map/editor/milsymbolSettings';
import {
    renderToDataURL,
    peekCachedDataURL,
    getAffiliationForSidc,
    buildEntityOptions,
    buildEntityTypeOptions,
    buildEntitySubTypeOptions,
    buildModifierOptions,
} from '../map/editor/renderer/SymbolRenderer';

// ---------------------------------------------------------------------------
// Static option lists derived from MIL-STD-2525D/E (no milSymbolSpec dependency)
// ---------------------------------------------------------------------------

const CONTEXTS = [
    { code: '0', label: 'Reality' },
    { code: '1', label: 'Exercise' },
    { code: '2', label: 'Simulation' },
];

const STANDARD_IDENTITIES = [
    { code: '0', label: 'Pending' },
    { code: '1', label: 'Unknown' },
    { code: '2', label: 'Assumed Friend' },
    { code: '3', label: 'Friend' },
    { code: '4', label: 'Neutral' },
    { code: '5', label: 'Suspect / Joker' },
    { code: '6', label: 'Hostile / Faker' },
];

const SYMBOL_SETS = [
    { code: '01', label: 'Air' },
    { code: '02', label: 'Air Missile' },
    { code: '05', label: 'Space' },
    { code: '06', label: 'Space Missile' },
    { code: '10', label: 'Land Unit' },
    { code: '11', label: 'Civilian Unit / Org' },
    { code: '15', label: 'Land Equipment' },
    { code: '20', label: 'Land Installation' },
    { code: '25', label: 'Control Measure' },
    { code: '27', label: 'Dismounted Individuals' },
    { code: '30', label: 'Sea Surface' },
    { code: '35', label: 'Sea Subsurface' },
    { code: '36', label: 'Mine Warfare' },
    { code: '40', label: 'Activities' },
    { code: '45', label: 'Atmospheric' },
    { code: '50', label: 'SIGINT' },
    { code: '52', label: 'CBRN' },
];

const STATUS_CODES = [
    { code: '0', label: 'Present' },
    { code: '1', label: 'Planned / Anticipated' },
    { code: '2', label: 'Fully Capable' },
    { code: '3', label: 'Damaged' },
    { code: '4', label: 'Destroyed' },
    { code: '5', label: 'Full to Capacity' },
];

// ponytail: echelon codes per 2525D Table A-I; mobility codes share the same SIDC digit position.
const ECHELON_CODES = [
    { code: '11', label: 'Team / Crew',          group: 'Echelon' },
    { code: '12', label: 'Squad',                group: 'Echelon' },
    { code: '13', label: 'Section',              group: 'Echelon' },
    { code: '14', label: 'Platoon',              group: 'Echelon' },
    { code: '15', label: 'Company / Battery',    group: 'Echelon' },
    { code: '16', label: 'Battalion',            group: 'Echelon' },
    { code: '17', label: 'Regiment / Group',     group: 'Echelon' },
    { code: '18', label: 'Brigade',              group: 'Echelon' },
    { code: '21', label: 'Division',             group: 'Echelon' },
    { code: '22', label: 'Corps',                group: 'Echelon' },
    { code: '23', label: 'Army',                 group: 'Echelon' },
    { code: '24', label: 'Army Group / Front',   group: 'Echelon' },
    { code: '25', label: 'Region / Theater',     group: 'Echelon' },
    { code: '26', label: 'Command',              group: 'Echelon' },
    { code: '31', label: 'Wheeled (Limited)',    group: 'Mobility' },
    { code: '32', label: 'Wheeled',              group: 'Mobility' },
    { code: '33', label: 'Tracked',              group: 'Mobility' },
    { code: '34', label: 'Wheeled & Tracked',    group: 'Mobility' },
    { code: '35', label: 'Towed',                group: 'Mobility' },
    { code: '36', label: 'Rail',                 group: 'Mobility' },
    { code: '51', label: 'Barge',                group: 'Mobility' },
    { code: '52', label: 'Amphibious',           group: 'Mobility' },
];

// ---------------------------------------------------------------------------
// SIDC helpers — MIL-STD-2525D 20-digit numeric format positions:
//   [0–1]   Version (always "10")
//   [2]     Context (0=Reality, 1=Exercise, 2=Sim)
//   [3]     Standard Identity (0=Pending … 6=Hostile)
//   [4–5]   Symbol Set (2 digits, e.g. "10"=Land Unit)
//   [6]     Status
//   [7]     HQ/TF/Feint
//   [8–9]   Echelon/Mobility
//   [10–11] Entity
//   [12–13] Entity Type
//   [14–15] Entity SubType
//   [16–17] Modifier 1
//   [18–19] Modifier 2
// ---------------------------------------------------------------------------

const HQTF_OPTIONS = [
    { code: '0', label: 'None' },
    { code: '2', label: 'Headquarters' },
    { code: '4', label: 'Task Force' },
    { code: '6', label: 'Task Force HQ' },
    { code: '1', label: 'Feint / Dummy' },
    { code: '3', label: 'Feint / Dummy HQ' },
    { code: '5', label: 'Feint / Dummy Task Force' },
    { code: '7', label: 'Feint / Dummy TF HQ' },
];

function parseSIDC(sidc) {
    if (!sidc || sidc.length < 20) return {
        context: '0', standardIdentity: '3', symbolSet: '10',
        status: '0', hqtf: '0', echelon: '00',
        entity: '00', entityType: '00', entitySubType: '00',
        mod1: '00', mod2: '00',
    };
    return {
        context:          sidc[2],
        standardIdentity: sidc[3],
        symbolSet:        sidc.slice(4, 6),
        status:           sidc[6],
        hqtf:             sidc[7],
        echelon:          sidc.slice(8, 10),
        entity:           sidc.slice(10, 12),
        entityType:       sidc.slice(12, 14),
        entitySubType:    sidc.slice(14, 16),
        mod1:             sidc.slice(16, 18),
        mod2:             sidc.slice(18, 20),
    };
}

function patchSIDC(sidc, patches) {
    if (!sidc || sidc.length < 20) return sidc;
    const arr = sidc.split('');
    if (patches.context          != null) arr[2] = patches.context;
    if (patches.standardIdentity != null) arr[3] = patches.standardIdentity;
    if (patches.symbolSet        != null) { arr[4] = patches.symbolSet[0]; arr[5] = patches.symbolSet[1]; }
    if (patches.status           != null) arr[6] = patches.status;
    if (patches.hqtf             != null) arr[7] = patches.hqtf;
    if (patches.echelon          != null) { arr[8] = patches.echelon[0]; arr[9] = patches.echelon[1]; }
    if (patches.entity           != null) { arr[10] = patches.entity[0]; arr[11] = patches.entity[1]; }
    if (patches.entityType       != null) { arr[12] = patches.entityType[0]; arr[13] = patches.entityType[1]; }
    if (patches.entitySubType    != null) { arr[14] = patches.entitySubType[0]; arr[15] = patches.entitySubType[1]; }
    if (patches.mod1             != null) { arr[16] = patches.mod1[0]; arr[17] = patches.mod1[1]; }
    if (patches.mod2             != null) { arr[18] = patches.mod2[0]; arr[19] = patches.mod2[1]; }
    return arr.join('');
}

const smallSelectSx = {
    fontSize: 12,
    '& .MuiSelect-select': {py: '4px', fontSize: 12},
};

// ---------------------------------------------------------------------------
// Manual coordinate entry helpers — Lat/Lon (decimal degrees) <-> MGRS.
// `mgrs` (v2.2.0, already used by coordGrid.js for grid labels) throws on invalid
// input/out-of-range latitudes (polar regions below 80°S/above 84°N), so every call is
// wrapped by the callers below rather than here.
// ---------------------------------------------------------------------------

function formatCoordNumber(n) {
    return Number.isFinite(n) ? String(Math.round(n * 1e6) / 1e6) : '';
}

function lonLatToMgrsString([lon, lat]) {
    try { return mgrsLib.forward([lon, lat], 5); } catch (_) { return ''; }
}

function parseLatLonText(latText, lonText) {
    const lat = parseFloat(latText);
    const lon = parseFloat(lonText);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return [lon, lat];
}

function parseMgrsText(mgrsText) {
    const trimmed = (mgrsText || '').trim();
    if (!trimmed) return null;
    try { return mgrsLib.toPoint(trimmed); } catch (_) { return null; }
}

// perf (mount then load data): the preview render below used to happen synchronously in a
// render-phase useMemo, blocking this panel's very first paint on every mount (i.e. every time a
// placed symbol is (re)selected on the map) on a genuine SVG-render cost. It's deferred to an
// idle-scheduled effect instead, mirroring ElementPalette's SymbolThumb pattern — requestIdleCallback
// isn't available in all browsers/environments (e.g. jsdom in tests), so fall back to a short
// setTimeout so scheduling still defers past the current paint.
const scheduleIdle = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
    ? (cb) => window.requestIdleCallback(cb)
    : (cb) => setTimeout(() => cb({didTimeout: false, timeRemaining: () => 0}), 0);

const cancelIdle = typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function'
    ? (id) => window.cancelIdleCallback(id)
    : (id) => clearTimeout(id);

function FieldRow({label, children}) {
    return (
        <Stack direction="row" alignItems="center" gap={1} sx={{minHeight: 34}}>
            <Typography variant="caption" sx={{width: 100, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>
                {label}
            </Typography>
            <Box sx={{flex: 1, minWidth: 0}}>
                {children}
            </Box>
        </Stack>
    );
}

function SectionLabel({children}) {
    return (
        <Typography variant="caption" sx={{
            display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
            textTransform: 'uppercase', color: 'text.disabled', mt: 1.5, mb: 0.5,
        }}>
            {children}
        </Typography>
    );
}

// ---------------------------------------------------------------------------
// Modifier label map — covers all MIL-STD-2525D amplifier keys.
// The Amplifiers accordion uses MSLookup.getMSLInfo(sidc).getModifiers() to
// get the live applicable set, then looks up human labels here.
// ---------------------------------------------------------------------------

const MODIFIER_LABELS = {
    [Modifiers.A_SYMBOL_ICON]:               'A – Symbol Icon',
    [Modifiers.B_ECHELON]:                   'B – Echelon',
    [Modifiers.C_QUANTITY]:                  'C – Quantity',
    [Modifiers.D_TASK_FORCE_INDICATOR]:      'D – Task Force',
    [Modifiers.E_FRAME_SHAPE_MODIFIER]:      'E – Frame Shape',
    [Modifiers.F_REINFORCED_REDUCED]:        'F – Reinforced/Reduced',
    [Modifiers.G_STAFF_COMMENTS]:            'G – Staff Comments',
    [Modifiers.H_ADDITIONAL_INFO_1]:         'H – Info 1',
    [Modifiers.H1_ADDITIONAL_INFO_2]:        'H1 – Info 2',
    [Modifiers.H2_ADDITIONAL_INFO_3]:        'H2 – Info 3',
    [Modifiers.J_EVALUATION_RATING]:         'J – Evaluation',
    [Modifiers.K_COMBAT_EFFECTIVENESS]:      'K – Combat Effectiveness',
    [Modifiers.L_SIGNATURE_EQUIP]:           'L – Signature Equip',
    [Modifiers.M_HIGHER_FORMATION]:          'M – Higher Formation',
    [Modifiers.N_HOSTILE]:                   'N – Hostile',
    [Modifiers.P_IFF_SIF_AIS]:               'P – IFF/SIF/AIS',
    [Modifiers.Q_DIRECTION_OF_MOVEMENT]:     'Q – Direction',
    [Modifiers.R_MOBILITY_INDICATOR]:        'R – Mobility',
    [Modifiers.R2_SIGNIT_MOBILITY_INDICATOR]:'R2 – SIGINT Mobility',
    [Modifiers.S_HQ_STAFF_INDICATOR]:        'S – HQ Staff',
    [Modifiers.S2_OFFSET_INDICATOR]:         'S2 – Offset',
    [Modifiers.T_UNIQUE_DESIGNATION_1]:      'T – Designation',
    [Modifiers.T1_UNIQUE_DESIGNATION_2]:     'T1 – Designation 2',
    [Modifiers.T2_UNIQUE_DESIGNATION_3]:     'T2 – Designation 3',
    [Modifiers.V_EQUIP_TYPE]:                'V – Equip Type',
    [Modifiers.W_DTG_1]:                     'W – DTG Start',
    [Modifiers.W1_DTG_2]:                    'W1 – DTG End',
    [Modifiers.X_ALTITUDE_DEPTH]:            'X – Alt/Depth',
    [Modifiers.Y_LOCATION]:                  'Y – Location',
    [Modifiers.Z_SPEED]:                     'Z – Speed',
    [Modifiers.AA_SPECIAL_C2_HQ]:            'AA – Special C2 HQ',
    [Modifiers.AB_FEINT_DUMMY_INDICATOR]:    'AB – Feint/Dummy',
    [Modifiers.AD_PLATFORM_TYPE]:            'AD – Platform Type',
    [Modifiers.AE_EQUIPMENT_TEARDOWN_TIME]:  'AE – Teardown Time',
    [Modifiers.AF_COMMON_IDENTIFIER]:        'AF – Common ID',
    [Modifiers.AG_AUX_EQUIP_INDICATOR]:      'AG – Aux Equip',
    [Modifiers.AH_AREA_OF_UNCERTAINTY]:      'AH – Area of Uncertainty',
    [Modifiers.AI_DEAD_RECKONING_TRAILER]:   'AI – Dead Reckoning',
    [Modifiers.AJ_SPEED_LEADER]:             'AJ – Speed Leader',
    [Modifiers.AK_PAIRING_LINE]:             'AK – Pairing Line',
    [Modifiers.AL_OPERATIONAL_CONDITION]:    'AL – Op Condition',
    [Modifiers.AM_DISTANCE]:                 'AM – Distance',
    [Modifiers.AN_AZIMUTH]:                  'AN – Azimuth',
    [Modifiers.AO_ENGAGEMENT_BAR]:           'AO – Engagement Bar',
    [Modifiers.AP_TARGET_NUMBER]:            'AP – Target No.',
    [Modifiers.AP1_TARGET_NUMBER_EXTENSION]: 'AP1 – Target Ext.',
    [Modifiers.AQ_GUARDED_UNIT]:             'AQ – Guarded Unit',
    [Modifiers.AR_SPECIAL_DESIGNATOR]:       'AR – Special Designator',
    [Modifiers.AS_COUNTRY]:                  'AS – Country',
    [Modifiers.AT_CAPACITY_OF_INSTALLATION]: 'AT – Capacity',
    [Modifiers.AV_LEADERSHIP]:               'AV – Leadership',
    [Modifiers.AW_HEADQUARTERS_ELEMENT]:     'AW – HQ Element',
    [Modifiers.AX_INSTALLATION_COMPOSITION]: 'AX – Installation',
    [Modifiers.AY_NETWORK_IDENTIFIER]:       'AY – Network ID',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SymbolEditor = ({feature, onUpdateProperties, onClose, onFlyToCoordinate}) => {
    const {settings} = useRendererSettings();
    const sidc = feature?.properties?.sidc || '';
    const parsed = useMemo(() => parseSIDC(sidc), [sidc]);

    const affiliationKey = getAffiliationForSidc(sidc) || 'friend';

    const patch = (sidcPatches) => onUpdateProperties?.({sidc: patchSIDC(sidc, sidcPatches)});

    // -----------------------------------------------------------------------
    // Manual coordinate entry (Position section) — Lat/Lon + MGRS text drafts,
    // kept in sync with `feature.geometry` but not clobbered mid-edit (tracked via
    // `*EditingRef`s, same "local draft, resync when not focused" pattern MapEditorModal
    // uses for trailWindowDays). Only meaningful for Point geometries — the only shape
    // this editor's placement flow (unit-icon symbols) ever produces.
    // -----------------------------------------------------------------------
    const coords = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : null;
    const [lon, lat] = coords || [null, null];

    const [latText, setLatText] = useState(() => formatCoordNumber(lat));
    const [lonText, setLonText] = useState(() => formatCoordNumber(lon));
    const [mgrsText, setMgrsText] = useState(() => (coords ? lonLatToMgrsString(coords) : ''));
    const latLonEditingRef = useRef(false);
    const mgrsEditingRef = useRef(false);

    useEffect(() => {
        if (latLonEditingRef.current) return;
        setLatText(formatCoordNumber(lat));
        setLonText(formatCoordNumber(lon));
    }, [lat, lon]);

    useEffect(() => {
        if (mgrsEditingRef.current) return;
        setMgrsText(coords ? lonLatToMgrsString(coords) : '');
    }, [lon, lat, coords]);

    const applyNewLonLat = (nextLonLat) => {
        onUpdateProperties?.({geometry: {type: 'Point', coordinates: nextLonLat}});
        onFlyToCoordinate?.(nextLonLat);
    };

    const commitLatLon = () => {
        latLonEditingRef.current = false;
        const parsedLonLat = parseLatLonText(latText, lonText);
        if (!parsedLonLat) {
            // Invalid — revert to last known-good values.
            setLatText(formatCoordNumber(lat));
            setLonText(formatCoordNumber(lon));
            return;
        }
        setMgrsText(lonLatToMgrsString(parsedLonLat));
        applyNewLonLat(parsedLonLat);
    };

    const commitMgrs = () => {
        mgrsEditingRef.current = false;
        const parsedLonLat = parseMgrsText(mgrsText);
        if (!parsedLonLat) {
            setMgrsText(coords ? lonLatToMgrsString(coords) : '');
            return;
        }
        const [nextLon, nextLat] = parsedLonLat;
        setLatText(formatCoordNumber(nextLat));
        setLonText(formatCoordNumber(nextLon));
        applyNewLonLat(parsedLonLat);
    };

    const handleLatLonKeyDown = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };
    const handleMgrsKeyDown = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };

    const amplifiers = useMemo(() => {
        const raw = feature?.properties?.sidcModifiers;
        if (!raw) return {};
        try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch (_) { return {}; }
    }, [feature?.properties?.sidcModifiers]);

    const patchAmplifiers = (key, value) => {
        const next = { ...amplifiers };
        if (value) next[key] = value; else delete next[key];
        onUpdateProperties?.({ sidcModifiers: JSON.stringify(next) });
    };

    // Reset dependent fields when symbolSet changes
    const handleSymbolSetChange = (symbolSet) => patch({
        symbolSet, entity: '00', entityType: '00', entitySubType: '00', mod1: '00', mod2: '00',
    });

    // Reset entityType/SubType when entity changes
    const handleEntityChange = (entity) => patch({entity, entityType: '00', entitySubType: '00'});

    // Reset entitySubType when entityType changes
    const handleEntityTypeChange = (entityType) => patch({entityType, entitySubType: '00'});

    // perf (mount then load data): cache-hit combos (already rendered — e.g. re-selecting a
    // symbol, or one whose exact sidc+amplifiers+settings combo was rendered elsewhere, such as
    // ElementPalette's own thumbnails) resolve synchronously so this panel's very first paint
    // already shows the right preview. A genuine cache miss instead paints the header/fields
    // immediately and defers the actual (comparatively expensive) SVG render to idle time,
    // swapping the image in once it resolves — see the `scheduleIdle` helper above.
    // `previewUrl === undefined` is the "loading" state (renders the placeholder below); `null`/
    // `''` means "no preview to show" (no sidc, or the render legitimately produced nothing).
    const previewMods = useMemo(() => ({forceAffiliation: affiliationKey, ...amplifiers}), [affiliationKey, amplifiers]);
    const [previewUrl, setPreviewUrl] = useState(() => (
        sidc ? peekCachedDataURL(sidc, previewMods, settings) : null
    ));

    useEffect(() => {
        if (!sidc) { setPreviewUrl(null); return undefined; }
        const cached = peekCachedDataURL(sidc, previewMods, settings);
        if (cached !== undefined) { setPreviewUrl(cached); return undefined; }

        setPreviewUrl(undefined); // loading placeholder while a fresh render is pending
        let cancelled = false;
        const handle = scheduleIdle(() => {
            if (cancelled) return;
            let result;
            try { result = renderToDataURL(sidc, previewMods, settings); } catch (_) { result = null; }
            setPreviewUrl(result);
        });
        return () => { cancelled = true; cancelIdle(handle); };
    }, [sidc, previewMods, settings]);

    // Derived option lists from MSLookup — memoised per symbolSet/entity/entityType
    const entities       = useMemo(() => buildEntityOptions(parsed.symbolSet),                                [parsed.symbolSet]);
    const entityTypes    = useMemo(() => buildEntityTypeOptions(parsed.symbolSet, parsed.entity),             [parsed.symbolSet, parsed.entity]);
    const entitySubTypes = useMemo(() => buildEntitySubTypeOptions(parsed.symbolSet, parsed.entity, parsed.entityType), [parsed.symbolSet, parsed.entity, parsed.entityType]);

    const echelonOptions = useMemo(() => {
        const isEquip = ['15', '30', '35'].includes(parsed.symbolSet);
        const isUnit  = ['10', '11', '01', '02'].includes(parsed.symbolSet);
        return [
            { code: '00', label: 'None' },
            ...ECHELON_CODES.filter(e =>
                isEquip ? e.group === 'Mobility' : isUnit ? e.group === 'Echelon' : true
            ),
        ];
    }, [parsed.symbolSet]);

    const sector1Options = useMemo(() => {
        const opts = buildModifierOptions(parsed.symbolSet, 1);
        return opts.length ? [{ code: '00', label: 'Unspecified' }, ...opts] : [{ code: '00', label: 'Unspecified' }];
    }, [parsed.symbolSet]);
    const sector2Options = useMemo(() => {
        const opts = buildModifierOptions(parsed.symbolSet, 2);
        return opts.length ? [{ code: '00', label: 'Unspecified' }, ...opts] : [{ code: '00', label: 'Unspecified' }];
    }, [parsed.symbolSet]);

    // Live per-SIDC amplifier keys from the renderer
    const ampFields = useMemo(() => {
        try {
            const lookup = MSLookup.getInstance();
            if (!lookup.isReady()) return [];
            const info = lookup.getMSLInfo(sidc);
            if (!info) return [];
            return info.getModifiers()
                .filter(key => MODIFIER_LABELS[key])
                .map(key => ({ key, label: MODIFIER_LABELS[key] }));
        } catch (_) { return []; }
    }, [sidc]);

    if (!sidc) return null;

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            {/* Header */}
            <Stack direction="row" alignItems="center" sx={(theme) => ({
                px: 1.5, py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0, bgcolor: 'background.default',
            })}>
                <Typography variant="caption" sx={{fontWeight: 700, flex: 1, fontSize: 11, letterSpacing: 0.3, textTransform: 'uppercase', color: 'text.disabled'}}>
                    Symbol Editor
                </Typography>
                {onClose && (
                    <Tooltip title="Close">
                        <IconButton size="small" sx={{p: 0.25}} onClick={onClose} aria-label="Close symbol editor">
                            <CloseRounded sx={{fontSize: 14}}/>
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <Box sx={{flex: 1, overflowY: 'auto', px: 1.5, py: 1}}>

                {/* Preview */}
                {previewUrl === undefined ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 72, mb: 1.5}}>
                        <CircularProgress size={28} thickness={5}/>
                    </Box>
                ) : previewUrl ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', mb: 1.5}}>
                        <img src={previewUrl} alt="Symbol preview" style={{maxHeight: 72}}/>
                    </Box>
                ) : null}

                {/* SIDC */}
                <Typography variant="caption" sx={{fontFamily: 'monospace', fontSize: 10, color: 'text.disabled', display: 'block', mb: 1, wordBreak: 'break-all'}}>
                    {sidc}
                </Typography>

                {coords && (
                    <>
                        <Divider sx={{mb: 1}}/>
                        <SectionLabel>Position</SectionLabel>

                        <FieldRow label="Latitude">
                            <TextField size="small" fullWidth
                                value={latText}
                                onChange={(e) => setLatText(e.target.value)}
                                onFocus={() => { latLonEditingRef.current = true; }}
                                onBlur={commitLatLon}
                                onKeyDown={handleLatLonKeyDown}
                                inputProps={{style: {fontSize: 12, padding: '3px 8px'}, 'aria-label': 'Latitude'}}
                            />
                        </FieldRow>

                        <FieldRow label="Longitude">
                            <TextField size="small" fullWidth
                                value={lonText}
                                onChange={(e) => setLonText(e.target.value)}
                                onFocus={() => { latLonEditingRef.current = true; }}
                                onBlur={commitLatLon}
                                onKeyDown={handleLatLonKeyDown}
                                inputProps={{style: {fontSize: 12, padding: '3px 8px'}, 'aria-label': 'Longitude'}}
                            />
                        </FieldRow>

                        <FieldRow label="MGRS">
                            <TextField size="small" fullWidth
                                value={mgrsText}
                                onChange={(e) => setMgrsText(e.target.value)}
                                onFocus={() => { mgrsEditingRef.current = true; }}
                                onBlur={commitMgrs}
                                onKeyDown={handleMgrsKeyDown}
                                inputProps={{style: {fontSize: 12, padding: '3px 8px', fontFamily: 'monospace'}, 'aria-label': 'MGRS grid reference'}}
                            />
                        </FieldRow>
                    </>
                )}

                <Divider sx={{mb: 1}}/>
                <SectionLabel>Identity</SectionLabel>

                <FieldRow label="Context">
                    <Select size="small" fullWidth value={parsed.context || '0'}
                        onChange={(e) => patch({context: e.target.value})} sx={smallSelectSx}>
                        {CONTEXTS.map((c) => (
                            <MenuItem key={c.code} value={c.code} sx={{fontSize: 12}}>{c.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                <FieldRow label="Std. Identity">
                    <Select size="small" fullWidth value={parsed.standardIdentity || '3'}
                        onChange={(e) => patch({standardIdentity: e.target.value})} sx={smallSelectSx}>
                        {STANDARD_IDENTITIES.map((si) => (
                            <MenuItem key={si.code} value={si.code} sx={{fontSize: 12}}>{si.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                <Divider sx={{my: 1}}/>
                <SectionLabel>Symbol</SectionLabel>

                <FieldRow label="Symbol Set">
                    <Select size="small" fullWidth value={parsed.symbolSet || '10'}
                        onChange={(e) => handleSymbolSetChange(e.target.value)} sx={smallSelectSx}>
                        {SYMBOL_SETS.map((s) => (
                            <MenuItem key={s.code} value={s.code} sx={{fontSize: 12}}>{s.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                <FieldRow label="Entity">
                    <Select size="small" fullWidth value={parsed.entity || '00'}
                        onChange={(e) => handleEntityChange(e.target.value)} sx={smallSelectSx}>
                        {entities.map((e) => (
                            <MenuItem key={e.code} value={e.code} sx={{fontSize: 12}}>{e.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                {entityTypes.length > 1 && (
                    <FieldRow label="Entity Type">
                        <Select size="small" fullWidth value={parsed.entityType || '00'}
                            onChange={(e) => handleEntityTypeChange(e.target.value)} sx={smallSelectSx}>
                            {entityTypes.map((t) => (
                                <MenuItem key={t.code} value={t.code} sx={{fontSize: 12}}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FieldRow>
                )}

                {entitySubTypes.length > 1 && (
                    <FieldRow label="Sub-Type">
                        <Select size="small" fullWidth value={parsed.entitySubType || '00'}
                            onChange={(e) => patch({entitySubType: e.target.value})} sx={smallSelectSx}>
                            {entitySubTypes.map((s) => (
                                <MenuItem key={s.code} value={s.code} sx={{fontSize: 12}}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FieldRow>
                )}

                <Divider sx={{my: 1}}/>
                <SectionLabel>Modifiers</SectionLabel>

                <FieldRow label="Status">
                    <Select size="small" fullWidth value={parsed.status || '0'}
                        onChange={(e) => patch({status: e.target.value})} sx={smallSelectSx}>
                        {STATUS_CODES.map((s) => (
                            <MenuItem key={s.code} value={s.code} sx={{fontSize: 12}}>{s.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                <FieldRow label="HQ / TF">
                    <Select size="small" fullWidth value={parsed.hqtf || '0'}
                        onChange={(e) => patch({hqtf: e.target.value})} sx={smallSelectSx}>
                        {HQTF_OPTIONS.map((h) => (
                            <MenuItem key={h.code} value={h.code} sx={{fontSize: 12}}>{h.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                <FieldRow label="Echelon">
                    <Select size="small" fullWidth value={parsed.echelon || '00'}
                        onChange={(e) => patch({echelon: e.target.value})} sx={smallSelectSx}>
                        {echelonOptions.map((e) => (
                            <MenuItem key={e.code} value={e.code} sx={{fontSize: 12}}>{e.label}</MenuItem>
                        ))}
                    </Select>
                </FieldRow>

                {sector1Options.length > 1 && (
                    <FieldRow label="Modifier 1">
                        <Select size="small" fullWidth value={parsed.mod1 || '00'}
                            onChange={(e) => patch({mod1: e.target.value})} sx={smallSelectSx}>
                            {sector1Options.map((m) => (
                                <MenuItem key={m.code} value={m.code} sx={{fontSize: 12}}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FieldRow>
                )}

                {sector2Options.length > 1 && (
                    <FieldRow label="Modifier 2">
                        <Select size="small" fullWidth value={parsed.mod2 || '00'}
                            onChange={(e) => patch({mod2: e.target.value})} sx={smallSelectSx}>
                            {sector2Options.map((m) => (
                                <MenuItem key={m.code} value={m.code} sx={{fontSize: 12}}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FieldRow>
                )}

                <Divider sx={{my: 1}}/>

                <Accordion disableGutters elevation={0} square
                    sx={{'&:before': {display: 'none'}, bgcolor: 'transparent'}}>
                    <AccordionSummary expandIcon={<ExpandMoreRounded sx={{fontSize: 14}}/>}
                        sx={{px: 0, minHeight: 28, '& .MuiAccordionSummary-content': {my: 0}}}>
                        <Typography variant="caption" sx={{
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                            textTransform: 'uppercase', color: 'text.disabled',
                        }}>
                            Amplifiers
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{px: 0, pt: 0, pb: 1}}>
                        {ampFields.map(({key, label}) => (
                            <FieldRow key={key} label={label}>
                                <TextField size="small" fullWidth
                                    value={amplifiers[key] || ''}
                                    onChange={(e) => patchAmplifiers(key, e.target.value)}
                                    inputProps={{style: {fontSize: 12, padding: '3px 8px'}}}
                                />
                            </FieldRow>
                        ))}
                    </AccordionDetails>
                </Accordion>

            </Box>
        </Stack>
    );
};

export default SymbolEditor;
